import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { gql } from "@apollo/client";
import { DateTime } from "luxon";
import { formattedPrice, Button, ButtonVariant, activeFulfillmentIdVar, benefitFulfillmentText, getCurrentUserId } from "@withfriendsco/wf-components";
import { DeliveryMethod } from "../types";
gql `
  fragment MembershipFulfillmentAddon on Addon {
    id
    description
    additionalCost {
      amount
    }
  }

  fragment MembershipFulfillment on Fulfillment {
    curated
    id
    fulfillmentDate
    isFulfilled
    platformIdKind
    productSelections {
      ...ChooseProductSelection
    }
    variant {
      id
      description
      additionalCost {
        amount
      }
      platformName
      platformId
    }
    addons {
      ...MembershipFulfillmentAddon
    }
    benefit {
      id
      name
      ... on SubscriptionBenefit {
        shippingIncluded
        deliveredToYour
      }
      variants {
        id
      }
      products {
        id
        variants {
          id
        }
      }
      ... on SubscriptionBenefit {
        claimable
      } 
      ... on WelcomeGiftBenefit {
        claimable
      }
      ... on DriveBenefit {
        claimable
      }
    }
    address {
      shippingName
      line1
      line2
      city
      state
      zip
      country
    }
    trackingUrl
    shippingNotes
    deliveryMethod
    claimedAt
    membership {
      id
      user {
        id
        emailAddress
      }
    }
  }
`;
const MembershipFulfillment = ({ fulfillment, retailDiscount, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const currentUserId = getCurrentUserId();
    const currentUserIsMember = ((_b = (_a = fulfillment.membership) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.id) == currentUserId;
    const dateTime = DateTime.fromSQL(fulfillment.fulfillmentDate);
    const discountMultiplier = (100 - retailDiscount) * 0.01;
    const missingOptions = (!fulfillment.variant && ((_c = fulfillment.benefit.variants) === null || _c === void 0 ? void 0 : _c.length)) || (((_d = fulfillment.productSelections) === null || _d === void 0 ? void 0 : _d.filter(selection => { var _a; return (_a = selection.variant) === null || _a === void 0 ? void 0 : _a.id; }).length) < fulfillment.benefit.products.filter(product => { var _a; return !!((_a = product.variants) === null || _a === void 0 ? void 0 : _a.length); }).length);
    const missingAddress = fulfillment.deliveryMethod != DeliveryMethod.Pickup && !(!!fulfillment.address.line1 && !!fulfillment.address.city && !!fulfillment.address.state && !!fulfillment.address.zip && !!fulfillment.address.country);
    let missingSteps = [];
    if (missingOptions)
        missingSteps.push("select an option");
    if (missingAddress)
        missingSteps.push("enter your address");
    return (_jsxs("div", { className: "border-2 shadow-md border-black p-4 " + (!fulfillment.isFulfilled ? "m-4" : "my-4"), children: [_jsxs("h4", { children: [((_e = fulfillment.benefit) === null || _e === void 0 ? void 0 : _e.__typename) == 'WelcomeGiftBenefit' ? "Welcome Gift" : "Subscription Box", ": ", fulfillment.benefit.name] }), _jsxs("div", { className: "border-t border-wfGray-300 mt-4 pt-4", children: [benefitFulfillmentText(fulfillment.benefit)('fulfillment_date'), ": ", dateTime.toLocaleString()] }), !fulfillment.isFulfilled ? (_jsx("div", { style: { marginBottom: "13px" }, children: _jsx("a", { target: "_blank", className: "text-wfGray-700 underline hover:text-wfGray-500", href: "https://help.withfriends.co/5364861-tracking-my-subscription-box", children: "Read about fulfillment dates" }) })) : fulfillment.trackingUrl ? (_jsx("div", { style: { marginBottom: "13px" }, children: currentUserIsMember ? (_jsx("a", { target: "_blank", className: "text-wfGray-700 underline hover:text-wfGray-500", href: fulfillment.trackingUrl, children: "Track your shipment" })) : (_jsx("span", { className: "text-wfGray-700", children: "Tracking information is available to the gift recipient." })) })) : null, (!!((_f = fulfillment.variant) === null || _f === void 0 ? void 0 : _f.description) ||
                ((_g = fulfillment.productSelections) === null || _g === void 0 ? void 0 : _g.some((productSelection) => { var _a; return (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.description; }))) && (_jsxs("div", { children: [!fulfillment.isFulfilled && "Current options selected: ", " ", fulfillment.productSelections
                        .filter((productSelection) => !!productSelection.variant)
                        .map((productSelection) => productSelection.variant.description)
                        .concat(fulfillment.variant ? [fulfillment.variant.description] : [])
                        .join(", ")] })), ((_h = fulfillment.addons) === null || _h === void 0 ? void 0 : _h.length) ? (_jsx("h4", { className: "border-t border-wfGray-300 mt-4 pt-4", children: "Addons Selected" })) : null, _jsx("ul", { children: (_j = fulfillment.addons) === null || _j === void 0 ? void 0 : _j.map((a) => {
                    return (_jsxs("li", { children: [a.description, ":", " ", _jsxs("span", { children: ["\u00A0", discountMultiplier == 1 ? (_jsx("span", { children: formattedPrice(a.additionalCost.amount) })) : (_jsxs(React.Fragment, { children: [_jsx("span", { className: "line-through", style: { textDecoration: "line-through" }, children: formattedPrice(a.additionalCost.amount) }), _jsxs("span", { className: "font-heavy", children: [" ", formattedPrice(a.additionalCost.amount * discountMultiplier)] })] }))] })] }, a.id));
                }) }), currentUserIsMember && (_jsx(_Fragment, { children: !missingAddress ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "font-bold mt-4 pt-4 border-t border-wfGray-300" }), _jsx("div", { children: fulfillment.deliveryMethod === DeliveryMethod.Pickup ? (_jsx("span", { children: "In-store pickup" })) : (_jsxs(_Fragment, { children: [((_k = fulfillment.address.shippingName) === null || _k === void 0 ? void 0 : _k.length) ? (_jsxs(_Fragment, { children: [fulfillment.address.shippingName, _jsx("br", {})] })) : null, fulfillment.address.line1, _jsx("br", {}), ((_l = fulfillment.address.line2) === null || _l === void 0 ? void 0 : _l.length) ? (_jsxs(_Fragment, { children: [fulfillment.address.line2, _jsx("br", {})] })) : null, fulfillment.address.city, ", ", fulfillment.address.state, " ", fulfillment.address.zip, _jsx("br", {}), fulfillment.address.country, fulfillment.shippingNotes ? (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx("br", {}), fulfillment.shippingNotes] })) : null] })) })] })) : ("") })), fulfillment.claimedAt ? (_jsx("div", { className: "my-4", children: "Claimed" })) : null, !fulfillment.isFulfilled && (_jsx(_Fragment, { children: _jsxs("div", { className: "font-bold mt-4 pt-4 border-t border-wfGray-300 my-2", children: [missingSteps.length ? (_jsx("div", { className: "font-bold text-lg text-wfRed-600", children: currentUserIsMember ? (_jsxs(_Fragment, { children: ["You need to ", missingSteps.join(" and "), " before this box can be shipped. Click the button below."] })) : (_jsxs(_Fragment, { children: ["Your gift recipient needs to ", missingSteps.join(" and "), " before this box can be shipped."] })) })) : "", currentUserIsMember && ((fulfillment.platformIdKind != 'Order' && (missingSteps.length ||
                            DateTime.fromSQL(fulfillment.fulfillmentDate) > DateTime.now().setZone('America/New_York') ||
                            (fulfillment.benefit.claimable && (!fulfillment.claimedAt ||
                                (fulfillment.claimedAt &&
                                    DateTime.fromISO(fulfillment.claimedAt).toFormat('yyyy-MM-dd') === DateTime.now().setZone('America/New_York').toFormat('yyyy-MM-dd')))))) ? (_jsx(Button, { className: "my-4", variant: ButtonVariant.Purple, onClick: () => {
                                window.parent.postMessage({ action: "scrollTo", value: 0 }, '*');
                                activeFulfillmentIdVar(fulfillment.id);
                            }, children: (fulfillment.benefit.claimable && !fulfillment.claimedAt && fulfillment.curated) ?
                                "Claim this shipment"
                                :
                                    "Edit this shipment" })) : (_jsxs("div", { className: "text-wfGray-500 my-4", children: [_jsx(Button, { className: "opacity-50 cursor-not-allowed", variant: ButtonVariant.Purple, disabled: true, children: "Edit this shipment" }), _jsx("p", { className: "mt-2", children: "It's too late to edit this shipment" })] })))] }) }))] }));
};
export default MembershipFulfillment;
